import { useState, useEffect, useCallback } from "react";
import { TrendingUp, TrendingDown, Wallet, Coins, DollarSign, Percent, Save, Zap } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const RANGES = [7, 30, 90];
const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ icon: Icon, label, value, tone, hint, testid }) {
  const toneCls = tone === "good" ? "text-green-600 dark:text-green-400"
    : tone === "bad" ? "text-destructive" : "";
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" /><span className="text-xs">{label}</span>
      </div>
      <div className={`mt-2 font-num text-2xl font-bold ${toneCls}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function AdminFinance() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [debts, setDebts] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/finance?days=${days}`).then(({ data }) => {
      setData(data);
      setCost(String(data.cost_per_credit));
    }).catch((e) => toast.error(apiError(e)));
  }, [days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/reseller-debts").then(({ data }) => setDebts(data)).catch(() => {}); }, []);

  const saveCost = async () => {
    const v = Number(cost);
    if (!(v >= 0)) return toast.error("Valor inválido");
    setSaving(true);
    try {
      await api.post("/admin/finance/cost", { cost_per_credit: v });
      toast.success("Custo por crédito atualizado");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const b = data?.revenue_breakdown || {};
  const profitTone = (data?.profit || 0) >= 0 ? "good" : "bad";

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Financeiro</h2>
          <p className="text-sm text-muted-foreground">Receita (PIX/recargas), custo dos créditos e lucro. Vendas de revenda não entram aqui.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`finance-range-${r}`}
              className={`rounded-md px-3 py-1 text-sm transition-colors ${days === r ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-reseller-debts">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-semibold">Revendedores em débito de créditos</h3>
          {debts && (debts.count > 0
            ? <span className="text-sm font-medium text-amber-500" data-testid="debts-total">{debts.count} revendedor(es) · deve {brl(debts.total_owed_brl)} ({debts.total_owed_credits} cr)</span>
            : <span className="text-sm font-medium text-green-600 dark:text-green-400" data-testid="debts-none">Nenhum 🎉</span>)}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Quando um revendedor vende sem créditos, a plataforma <b>adianta</b> o custo (o crédito dele fica negativo).
          A venda é servida normalmente e a dívida <b>quita sozinha</b> na próxima recarga.
        </p>
        {debts?.count > 0 && (
          <div className="mt-3 space-y-2">
            {debts.items.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 p-3 text-sm" data-testid={`debt-row-${d.id}`}>
                <div className="min-w-0 pr-3">
                  <div className="truncate font-medium">{d.name || d.email}</div>
                  <div className="truncate text-xs text-muted-foreground">{d.email} · {d.role}</div>
                </div>
                <div className="text-right">
                  <div className="font-num font-semibold text-destructive">{d.owed_credits} cr</div>
                  <div className="text-xs text-muted-foreground">{brl(d.owed_brl)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1.5">
          <Label>Custo por crédito (R$) — quanto o crédito custa pra vocês</Label>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)}
              className="w-40" data-testid="finance-cost-input" />
            <Button onClick={saveCost} disabled={saving} className="gap-2" data-testid="finance-cost-save">
              <Save className="h-4 w-4" /> Salvar
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Custo = <b>custo por crédito</b> × créditos consumidos em ativações <b>aprovadas</b> no período.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={DollarSign} label="Entrou (receita)" value={brl(data?.revenue)} testid="finance-kpi-revenue"
          hint={`${days} dias · PIX + recargas`} />
        <Kpi icon={Coins} label="Custo dos créditos" value={brl(data?.cost)} testid="finance-kpi-cost"
          hint={`${data?.credits_consumed ?? 0} créd. × ${brl(data?.cost_per_credit)}`} />
        <Kpi icon={profitTone === "good" ? TrendingUp : TrendingDown} label="Lucro" value={brl(data?.profit)}
          tone={profitTone} testid="finance-kpi-profit" hint={`${data?.activations_approved ?? 0} ativações aprovadas`} />
        <Kpi icon={Percent} label="Margem" value={`${data?.margin ?? 0}%`} tone={profitTone} testid="finance-kpi-margin" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <h3 className="mb-4 font-heading font-semibold">Receita por dia</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.series || []}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={48} />
                <Tooltip formatter={(v) => brl(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="mb-4 font-heading font-semibold">De onde veio a receita</h3>
          <div className="space-y-3">
            <Row icon={Zap} label="Ativações via PIX" value={brl(b.activation_pix)} />
            <Row icon={Wallet} label="Recargas de saldo" value={brl(b.recharge_balance)} />
            <Row icon={Coins} label="Compras de crédito" value={brl(b.recharge_credits)} />
            <div className="mt-3 border-t border-border pt-3">
              <Row icon={DollarSign} label="Total que entrou" value={brl(data?.revenue)} bold />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className={`font-num ${bold ? "font-bold" : ""}`}>{value}</div>
    </div>
  );
}
